import {
  boolean,
  freedraw,
  general,
  grid,
  retouching,
  useEditorStore,
} from '@modules/editor';
import {
  type GenericElement,
  deleteMultipleElements,
  updateElement,
  updateMultipleElements,
} from '@modules/editor/src/lib/element';
import {
  renderInteractiveScene,
  renderStaticScene,
} from '@modules/editor/src/lib/scene';
import { type BooleanElement } from '@modules/editor/src/plugins/boolean';
import { type GridElement } from '@modules/editor/src/plugins/grid';
import { useEditorRuntimeStore } from '@modules/editor/src/stores/useEditorRuntimeStore';
import { createSelectorHooks } from 'auto-zustand-selectors-hook';
import { FastAverageColor } from 'fast-average-color';
import { type RefObject } from 'react';
import { create } from 'zustand';

import { t } from '$/components/core/Localization/18n';
import { trackEvent } from '$/logger';
import {
  getNextLayerForComponent,
  reassignAllComponentLayers,
} from '$/pages/CustomEditorPage/utils/layerAssignment';
import {
  deriveActiveComponentId,
  deriveSelectedComponentIds,
  isUserGeneratedElement,
} from '$/pages/CustomEditorPage/utils/selection';
import { type Material } from '$/services/usecases/materials/mapper/materials';
import { type EditorProject } from '$/services/usecases/projects/mapper/editorProject';
import { useCollectionStore } from '$/stores/useCollectionStore';
import { trackMaterialRendering } from '$/utils/piwikUtils';
import { uid } from '$/utils/uid';

const mmToCmConversionFactor = 10;
const minimalDimensionSize = 500;

export type EditorComponent = {
  id: string;
  elements: string[];
  color: string;
  materialId?: string;
  shouldDisplayTexture: boolean;
  name: string;
};

type State = {
  components: EditorComponent[];
  projectName: string;
  referenceMeasure: number;
  openedAccordionId: string | null;
  autosaveTimerRef: RefObject<NodeJS.Timeout | null>;
  project: EditorProject | null;
  fullscreen: boolean;
};

type Actions = {
  addNewComponent: () => void;
  deleteComponent: (id: string) => void;
  deleteComponents: (ids: string[]) => void;
  mergeComponents: (componentIds?: string[]) => void;
  associateMaterialWithComponent: (material: Material) => Promise<void>;
  associateElementWithComponent: (
    elementId: string,
    componentId?: string,
  ) => EditorComponent | null;
  disassociateElementWithComponent: (elementId: string) => void;
  moveComponent: (componentId: string, newIndex: number) => void;
  moveElement: (elementId: string, newIndex: number) => void;
  resetStore: () => void;
  toggleFullscreen: () => void;
};

let cachedActiveComponentId = '';

/**
 * Returns the active component, falling back to the last known value
 * when the current selection doesn't map to a component
 */
export const getActiveComponentId = (): string => {
  const { components } = store.getState();
  const { selectedElementIds } = useEditorStore.getState();
  const derived = deriveActiveComponentId(components, selectedElementIds);

  if (derived) {
    cachedActiveComponentId = derived;
    return derived;
  }

  return cachedActiveComponentId;
};

const initialState: State = {
  components: [],
  projectName: '',
  referenceMeasure: 0,
  openedAccordionId: null,
  autosaveTimerRef: { current: null },
  project: null,
  fullscreen: false,
};
const store = create<State & Actions>()((set, get) => {
  const createNewComponent = (letterNumber?: number): EditorComponent => {
    const { components } = get();
    let letter = letterNumber ?? components.length + 1;
    let name = t('editor.component_name', {
      letter,
      defaultValue: 'Bauteil {{letter}}',
    });
    let maxIterations = components.length + 200;
    const existingNames = new Set(components.map((c) => c.name));

    while (existingNames.has(name) && maxIterations-- > 0) {
      letter++;
      name = t('editor.component_name', {
        letter,
        defaultValue: 'Bauteil {{letter}}',
      });
      if (!name.includes(letter.toString())) break;
    }

    if (maxIterations <= 0) {
      console.warn('Max iterations reached while creating new component');
    }

    return {
      id: uid(),
      elements: [],
      color: '#3c87f7',
      shouldDisplayTexture: false,
      name,
    };
  };

  return {
    ...initialState,
    resetStore: () => {
      const currentTimeout = get().autosaveTimerRef?.current;
      if (currentTimeout) clearTimeout(currentTimeout);
      cachedActiveComponentId = '';
      set({ ...initialState, autosaveTimerRef: { current: null } });
    },
    addNewComponent: () => {
      const newComponent = createNewComponent();

      set((state) => ({
        components: [...state.components, newComponent],
        openedAccordionId: newComponent.id,
      }));

      boolean.actions.createBooleanGroup({
        id: newComponent.id,
        layer: getNextLayerForComponent(newComponent.id),
      });

      general.actions.captureHistorySnapshot();
    },
    deleteComponent: (id) => {
      get().deleteComponents([id]);
    },
    deleteComponents: (ids) => {
      if (useEditorRuntimeStore.getState().readonly) return;

      const state = get();
      if (ids.length === 0) return;

      const idsToDelete = new Set(ids);

      const elementsToDelete: string[] = [];
      for (const id of idsToDelete) {
        const component = state.components.find((c) => c.id === id);
        if (component) {
          elementsToDelete.push(...component.elements);
        }
      }

      useEditorStore.setState({ selectedElementIds: [] });

      if (elementsToDelete.length > 0) {
        general.actions.deleteElements(elementsToDelete);
      }

      const newComponents = state.components.filter(
        (c) => !idsToDelete.has(c.id),
      );

      if (newComponents.length === 0) {
        const newComponent = createNewComponent(1);

        set({
          components: [newComponent],
          openedAccordionId: newComponent.id,
        });

        boolean.actions.createBooleanGroup({
          id: newComponent.id,
          layer: getNextLayerForComponent(newComponent.id),
        });

        general.actions.captureHistorySnapshot();
        return;
      }

      const firstDeletedIndex = state.components.findIndex((c) =>
        idsToDelete.has(c.id),
      );
      const newIndex = Math.min(firstDeletedIndex, newComponents.length - 1);
      const newActiveComponentId = newComponents[newIndex].id;

      useEditorStore.setState({
        selectedElementIds: [newActiveComponentId],
      });

      set({
        components: newComponents,
        openedAccordionId: idsToDelete.has(state.openedAccordionId ?? '')
          ? newActiveComponentId
          : state.openedAccordionId,
      });
      general.actions.captureHistorySnapshot();
    },
    mergeComponents: (
      componentIds = deriveSelectedComponentIds(
        get().components,
        useEditorStore.getState().selectedElementIds,
      ),
    ) => {
      if (useEditorRuntimeStore.getState().readonly) return;

      const state = get();
      if (componentIds.length < 2) return;

      general.actions.pauseHistory();

      const activeComponentId = deriveActiveComponentId(
        state.components,
        useEditorStore.getState().selectedElementIds,
      );
      const activeComponent = state.components.find(
        (c) => c.id === activeComponentId,
      );

      const allElements = useEditorStore.getState().elements;
      const elementsToMove: GenericElement[] = [];

      for (const componentId of componentIds) {
        const component = state.components.find((c) => c.id === componentId);
        if (!component) continue;
        for (const elementId of component.elements) {
          const element = allElements.get(elementId);
          if (!isUserGeneratedElement(element)) continue;
          elementsToMove.push(element);
        }
      }

      const newComponent = createNewComponent();
      newComponent.color = activeComponent?.color ?? '#3c87f7';
      newComponent.materialId = activeComponent?.materialId;
      newComponent.shouldDisplayTexture =
        activeComponent?.shouldDisplayTexture ?? false;

      set({ components: [...state.components, newComponent] });

      boolean.actions.createBooleanGroup({
        id: newComponent.id,
        layer: getNextLayerForComponent(newComponent.id),
        fillColor: newComponent.color,
      });

      if (elementsToMove.length > 0) {
        updateMultipleElements(
          allElements,
          elementsToMove,
          elementsToMove.map(() => ({
            renderedById: newComponent.id,
            strokeColor: newComponent.color,
          })),
        );
      }

      set((s) => ({
        components: s.components.map((c) =>
          c.id === newComponent.id
            ? {
                ...c,
                elements: [...c.elements, ...elementsToMove.map((e) => e.id)],
              }
            : c,
        ),
      }));

      const movedElementIds = new Set(elementsToMove.map((e) => e.id));
      const elementIdsToDelete: string[] = [];
      for (const componentId of componentIds) {
        const component = state.components.find((c) => c.id === componentId);
        if (!component) continue;
        for (const elementId of component.elements) {
          if (!movedElementIds.has(elementId)) {
            elementIdsToDelete.push(elementId);
          }
        }
      }

      if (elementIdsToDelete.length > 0) {
        deleteMultipleElements(allElements, elementIdsToDelete);
      }

      set((s) => ({
        components: s.components.filter((c) => !componentIds.includes(c.id)),
      }));

      reassignAllComponentLayers();
      boolean.actions.reevaluateDimensions(newComponent.id);

      useEditorStore.setState({
        selectedElementIds: [newComponent.id],
        polygonPointsVisible: false,
      });
      set({ openedAccordionId: newComponent.id });

      renderStaticScene();
      renderInteractiveScene();

      general.actions.resumeHistory();
      general.actions.captureHistorySnapshot();
    },
    associateMaterialWithComponent: async (material) => {
      const state = get();
      const activeComponentId = getActiveComponentId();

      const componentIndex = state.components.findIndex(
        (component) => component.id === activeComponentId,
      );

      if (componentIndex === -1) return;

      const newComponents = [...state.components];

      newComponents[componentIndex] = { ...newComponents[componentIndex] };
      newComponents[componentIndex].materialId = material.id;

      const materialUrl = material.seamlessTextureUrl();

      let color = material.hex;
      let shouldDisplayTexture = false;

      if (materialUrl) {
        color = (await new FastAverageColor().getColorAsync(materialUrl)).hex;
        shouldDisplayTexture = true;
      }

      newComponents[componentIndex].color = color;
      newComponents[componentIndex].shouldDisplayTexture = shouldDisplayTexture;
      for (const id of newComponents[componentIndex].elements) {
        if (!id.startsWith('grid')) {
          freedraw.actions.updateColorForElement(id, color);
        }

        if (id === activeComponentId) {
          const element = useEditorStore.getState().elements.get(id) as
            | BooleanElement
            | undefined;

          if (element?.type !== 'boolean') continue;

          updateElement(useEditorStore.getState().elements, element, {
            fillColor: color,
          });
        }

        if (id === `grid-${activeComponentId}`) {
          const element = useEditorStore.getState().elements.get(id) as
            | GridElement
            | undefined;

          if (!element) continue;

          await grid.actions.setMaterial(
            element,
            materialUrl
              ? {
                  imageUrl: materialUrl,
                  textureRealX: material.textureWidth
                    ? material.textureWidth / mmToCmConversionFactor
                    : minimalDimensionSize,
                  textureRealY: material.textureHeight
                    ? material.textureHeight / mmToCmConversionFactor
                    : minimalDimensionSize,
                }
              : null,
          );
        }
      }

      set({ components: newComponents });

      useCollectionStore.setState({
        activeType: material.type[0],
        appliedFilterOptions: [
          { option: 'producer', value: material.producer },
          {
            option: 'collection',
            value: material.collection,
          },
        ],
      });

      trackMaterialRendering('Editor', material);

      general.actions.captureHistorySnapshot();
    },
    associateElementWithComponent: (
      elementId,
      componentId = getActiveComponentId(),
    ) => {
      const state = get();

      let componentIndex = state.components.findIndex(
        (component) => component.id === componentId,
      );

      if (componentIndex === -1) {
        if (state.components.length === 0) return null;

        componentIndex = state.components.length - 1;
      }

      const newComponents = [...state.components];
      newComponents[componentIndex] = {
        ...newComponents[componentIndex],
        elements: [...newComponents[componentIndex].elements, elementId],
      };

      set({
        components: newComponents,
      });

      return newComponents[componentIndex];
    },
    disassociateElementWithComponent: (elementId) => {
      set((state) => {
        const componentIndex = state.components.findIndex((component) =>
          component.elements.includes(elementId),
        );

        if (componentIndex === -1) return state;

        const newComponents = [...state.components];
        newComponents[componentIndex] = {
          ...newComponents[componentIndex],
          elements: newComponents[componentIndex].elements.filter(
            (id) => id !== elementId,
          ),
        };

        return {
          components: newComponents,
        };
      });
    },
    moveComponent: (componentId, newIndex) => {
      set((state) => {
        const componentIndex = state.components.findIndex(
          (component) => component.id === componentId,
        );

        if (
          componentIndex === -1 ||
          newIndex < 0 ||
          newIndex >= state.components.length
        ) {
          return state;
        }

        const newComponents = [...state.components];
        const [movedComponent] = newComponents.splice(componentIndex, 1);
        newComponents.splice(newIndex, 0, movedComponent);

        return {
          components: newComponents,
          openedAccordionId: movedComponent.id,
        };
      });
      general.actions.captureHistorySnapshot();
    },
    moveElement: () => {},
    toggleFullscreen: () => {
      set((state) => ({ fullscreen: !state.fullscreen }));
      trackEvent('Projects', 'Fullscreen', 'Editor');
    },
  };
});

const updateStrokeColors = () => {
  const activeComponentId = getActiveComponentId();

  const component = store
    .getState()
    .components.find((component) => component.id === activeComponentId);

  if (!component) return;

  freedraw.actions.setStrokeColor(component.color);
  retouching.actions.setStencilColor(component.color);
};

store.subscribe(updateStrokeColors);
useEditorStore.subscribe(updateStrokeColors);

export const useCustomEditorStore = createSelectorHooks(store);
